import type { components } from '@joyful/openapi/types.gen'

export type AgentChatSessionKind =
  | 'error'
  | 'follow-up-queued'
  | 'ready'
  | 'reconnecting'
  | 'stopping'
  | 'streaming'
  | 'submitted'

export type ChatSessionState =
  | { readonly kind: Exclude<AgentChatSessionKind, 'follow-up-queued'> }
  | {
      readonly conversationId: string
      readonly kind: 'follow-up-queued'
    }

export type AgentChatStatus = Exclude<AgentChatSessionKind, 'follow-up-queued'>
export type AgentChatRunStatus =
  | 'awaiting_human'
  | 'failed'
  | 'hibernating'
  | 'running'

type EncounterOwner = components['schemas']['EncounterOwner']

export type AgentChatActivityKind =
  | 'error'
  | 'offline'
  | 'queued'
  | 'reconnecting'
  | 'sending'
  | 'stopping'
  | 'working'

export interface AgentChatActivity {
  readonly kind: AgentChatActivityKind
  readonly label: string
}

export function isAgentChatInFlight(status: AgentChatStatus) {
  return isAgentChatBusySessionKind(status)
}

export function isAgentChatSessionBusy(sessionKind: AgentChatSessionKind) {
  return isAgentChatBusySessionKind(sessionKind)
}

export function getAgentChatActivityLabelMotion(
  activity: AgentChatActivity | null,
) {
  if (activity?.kind === 'error' || activity?.kind === 'offline') {
    return 'static'
  }

  return 'shimmer'
}

export function isAgentRunInFlight(
  status: AgentChatRunStatus | null | undefined,
) {
  return status === 'running' || status === 'hibernating'
}

export function didAgentRunHandOffToHuman(
  previousRunStatus: AgentChatRunStatus | null | undefined,
  nextRunStatus: AgentChatRunStatus | null,
) {
  const isHandedOffToHuman =
    nextRunStatus === 'awaiting_human' || nextRunStatus === 'failed'
  return isHandedOffToHuman && isAgentRunInFlight(previousRunStatus)
}

export function didEncounterOwnerChange(
  previousOwner: EncounterOwner | null | undefined,
  nextOwner: EncounterOwner | null | undefined,
) {
  if (previousOwner === undefined || nextOwner === undefined) {
    return false
  }

  return previousOwner !== nextOwner
}

export function getChatSessionStatus(
  sessionState: ChatSessionState,
): AgentChatStatus {
  if (sessionState.kind === 'follow-up-queued') {
    return 'ready'
  }

  return sessionState.kind
}

export function toChatSessionState(status: AgentChatStatus): ChatSessionState {
  return { kind: status }
}

export function shouldTreatAgentChatStreamDropAsRecoverable({
  isAgentRunInFlight = false,
  isDisconnect,
  isError = false,
}: {
  readonly isAgentRunInFlight?: boolean
  readonly isDisconnect: boolean
  readonly isError?: boolean
}) {
  return isAgentRunInFlight && (isDisconnect || isError)
}

export function getAgentChatStreamDropSessionState({
  currentState,
  hasReceivedStreamData,
  isRecoverableDrop,
  isStreamFailure,
  latestRunStatus,
  wasIntentionalStop,
}: {
  readonly currentState: ChatSessionState
  readonly hasReceivedStreamData: boolean
  readonly isRecoverableDrop: boolean
  readonly isStreamFailure: boolean
  readonly latestRunStatus: AgentChatRunStatus | null
  readonly wasIntentionalStop: boolean
}): ChatSessionState {
  if (wasIntentionalStop) {
    if (currentState.kind === 'stopping') {
      return currentState
    }

    return { kind: 'ready' }
  }

  if (currentState.kind === 'follow-up-queued') {
    return currentState
  }

  if (isRecoverableDrop) {
    return { kind: 'reconnecting' }
  }

  if (isStreamFailure) {
    if (latestRunStatus === 'awaiting_human' && hasReceivedStreamData) {
      return { kind: 'ready' }
    }

    return { kind: 'error' }
  }

  return { kind: 'ready' }
}

export function getAgentQueuedMessageDrainMode({
  isAgentRunInFlight = false,
  sessionKind,
}: {
  readonly isAgentRunInFlight?: boolean
  readonly sessionKind: AgentChatSessionKind
}): 'drain-ready' | 'queueing' {
  if (isAgentChatBusySessionKind(sessionKind) || isAgentRunInFlight) {
    return 'queueing'
  }

  return 'drain-ready'
}

export function getAgentChatActivity({
  isAgentRunInFlight = false,
  isAwaitingFirstAssistantFrame = false,
  isFollowUpQueued,
  isOnline,
  isPreparingAssistantResponse,
  sessionKind,
}: {
  readonly isAgentRunInFlight?: boolean
  readonly isAwaitingFirstAssistantFrame?: boolean
  readonly isFollowUpQueued: boolean
  readonly isOnline: boolean
  readonly isPreparingAssistantResponse: boolean
  readonly sessionKind: AgentChatSessionKind
}): AgentChatActivity | null {
  if (!isOnline) {
    return { kind: 'offline', label: `You're offline` }
  }

  if (sessionKind === 'stopping') {
    return { kind: 'stopping', label: `Stopping…` }
  }

  const isReconnectingSession =
    sessionKind === 'reconnecting' ||
    (sessionKind === 'error' && isAgentRunInFlight)

  if (isReconnectingSession) {
    return { kind: 'reconnecting', label: `Reconnecting…` }
  }

  if (sessionKind === 'error') {
    return {
      kind: 'error',
      label: `Something went wrong. Try sending your message again.`,
    }
  }

  if (isFollowUpQueued || sessionKind === 'follow-up-queued') {
    return {
      kind: 'queued',
      label: `Queued. Resuming…`,
    }
  }

  if (sessionKind === 'submitted' && isAwaitingFirstAssistantFrame) {
    return { kind: 'sending', label: `Sending…` }
  }

  if (isPreparingAssistantResponse) {
    return { kind: 'working', label: `Working…` }
  }

  return null
}

function isAgentChatBusySessionKind(sessionKind: AgentChatSessionKind) {
  return (
    sessionKind === 'follow-up-queued' ||
    sessionKind === 'reconnecting' ||
    sessionKind === 'stopping' ||
    sessionKind === 'streaming' ||
    sessionKind === 'submitted'
  )
}
